import { CdkConnectedOverlay, OverlayModule } from "@angular/cdk/overlay";
import { NgClass } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  input,
  signal,
  ViewChild,
} from "@angular/core";
import { RouterModule } from "@angular/router";
import { IconChevronRightComponent } from "@optee/icons/icon-chevron-right.component";
import { ButtonComponent } from "@optee/ui/components/button/button.component";
import { NAV_ITEMS, NavAction, NavPage } from "../navigation-items";
import { ServicesMenuComponent } from "../services-menu/services-menu.component";
import { YouAreMenuComponent } from "../you-are-menu/you-are-menu.component";

@Component({
  selector: "swc-header-desktop",
  template: `
    <nav
      class="font-display flex w-screen select-none items-center justify-between px-16 py-10"
    >
      <div class="flex items-center gap-14">
        <a routerLink="/">
          <span class="sr-only">Optee</span>
          <img
            class="h-10 w-auto"
            alt="Logo de Optee"
            [src]="
              theme() === 'dark'
                ? '/assets/logo-dark-theme.svg'
                : '/assets/logo-light-theme.svg'
            "
          />
        </a>
        <div class="flex gap-4 xl:gap-8">
          @for (item of NAV_ITEMS; track item.slug) {
            @if (item.type === "page") {
              <a
                class="leading-6"
                [ngClass]="{
                  'text-white': theme() === 'dark',
                  'text-primary-500': currentPage().startsWith(item.slug),
                }"
                [routerLink]="item.url"
              >
                {{ item.label }}
              </a>
            }
            @if (item.type === "action") {
              @if (item.slug === NavAction.SERVICES) {
                <span
                  class="flex cursor-pointer items-center gap-2 leading-6"
                  #trigger="cdkOverlayOrigin"
                  cdkOverlayOrigin
                  (click)="isServicesMenuOpen.set(true)"
                  [ngClass]="{
                    'text-white': theme() === 'dark',
                  }"
                >
                  {{ item.label }}

                  <icon-chevron-right
                    class="size-4 origin-center rotate-90 transition-transform duration-200"
                    [ngClass]="{
                      'text-primary-500': theme() === 'light',
                      'rotate-0': isServicesMenuOpen(),
                    }"
                  />
                </span>

                <ng-template
                  cdkConnectedOverlay
                  cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
                  cdkConnectedOverlayTransformOriginOn="swc-services-menu"
                  (backdropClick)="servicesMenuComponent.close()"
                  (detach)="isServicesMenuOpen.set(false)"
                  [cdkConnectedOverlayHasBackdrop]="true"
                  [cdkConnectedOverlayOffsetY]="10"
                  [cdkConnectedOverlayOpen]="isServicesMenuOpen()"
                  [cdkConnectedOverlayOrigin]="trigger"
                >
                  <swc-services-menu (closed)="isServicesMenuOpen.set(false)" />
                </ng-template>
              }
              @if (item.slug === NavAction.SOLUTIONS) {
                <span
                  class="flex cursor-pointer items-center gap-2 leading-6"
                  #trigger="cdkOverlayOrigin"
                  cdkOverlayOrigin
                  (click)="isYouAreMenuOpen.set(true)"
                  [ngClass]="{
                    'text-white': theme() === 'dark',
                  }"
                >
                  {{ item.label }}

                  <icon-chevron-right
                    class="size-4 origin-center rotate-90 transition-transform duration-200"
                    [ngClass]="{
                      'text-primary-500': theme() === 'light',
                      'rotate-0': isYouAreMenuOpen(),
                    }"
                  />
                </span>

                <ng-template
                  cdkConnectedOverlay
                  cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
                  cdkConnectedOverlayTransformOriginOn="swc-you-are-menu"
                  (backdropClick)="youAreMenuComponent.close()"
                  (detach)="isYouAreMenuOpen.set(false)"
                  [cdkConnectedOverlayHasBackdrop]="true"
                  [cdkConnectedOverlayOffsetY]="10"
                  [cdkConnectedOverlayOpen]="isYouAreMenuOpen()"
                  [cdkConnectedOverlayOrigin]="trigger"
                >
                  <swc-you-are-menu (closed)="isYouAreMenuOpen.set(false)" />
                </ng-template>
              }
            }
          }
        </div>
      </div>

      <div class="flex items-center gap-4 xl:gap-8">
        <a
          href="https://app.optee.io/auth"
          target="_blank"
          [ngClass]="{
            'text-white': theme() === 'dark',
          }"
        >
          Connexion
        </a>
        <oui-button
          href="/demo"
          [variant]="theme() === 'dark' ? 'standard' : 'primary'"
        >
          Demander une démo
        </oui-button>
      </div>
    </nav>
  `,
  imports: [
    NgClass,
    RouterModule,
    ServicesMenuComponent,
    YouAreMenuComponent,
    ButtonComponent,
    IconChevronRightComponent,
    OverlayModule,
    CdkConnectedOverlay,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderDesktopComponent {
  currentPage = input.required<NavPage>();
  theme = input<"light" | "dark">("dark");

  @ViewChild(ServicesMenuComponent)
  servicesMenuComponent!: ServicesMenuComponent;

  @ViewChild(YouAreMenuComponent)
  youAreMenuComponent!: YouAreMenuComponent;

  isServicesMenuOpen = signal(false);
  isYouAreMenuOpen = signal(false);

  NAV_ITEMS = NAV_ITEMS;
  NavAction = NavAction;
  NavPage = NavPage;
}
